/**
 * SupabaseQuickRepliesRepository
 * Responsabilidade: Acesso a dados de respostas rápidas no Supabase
 * Princípios: Single Responsibility, Dependency Inversion
 */

import { supabase } from '../../lib/supabase';
import { QuickReply } from '../../types';

export class SupabaseQuickRepliesRepository {
    async findAll(organizationId: string): Promise<QuickReply[]> {
        const { data, error } = await supabase
            .from('quick_replies')
            .select('*')
            .eq('organization_id', organizationId)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('[QuickRepliesRepository] Error fetching quick replies:', error);
            throw new Error(`Erro ao buscar respostas rápidas: ${error.message}`);
        }

        return (data || []).map(this.mapToEntity);
    }

    async findById(id: string, organizationId: string): Promise<QuickReply | null> {
        const { data, error } = await supabase
            .from('quick_replies')
            .select('*')
            .eq('id', id)
            .eq('organization_id', organizationId)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null;
            console.error('[QuickRepliesRepository] Error fetching quick reply:', error);
            throw new Error(`Erro ao buscar resposta rápida: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async create(quickReply: Omit<QuickReply, 'id' | 'createdAt'>, organizationId: string): Promise<QuickReply> {
        const { data, error } = await supabase
            .from('quick_replies')
            .insert([{
                organization_id: organizationId,
                shortcut: quickReply.shortcut,
                message: quickReply.message
            }])
            .select()
            .single();

        if (error) {
            console.error('[QuickRepliesRepository] Error creating quick reply:', error);
            throw new Error(`Erro ao criar resposta rápida: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async update(id: string, quickReply: Partial<QuickReply>, organizationId: string): Promise<QuickReply> {
        const updateData: any = {};
        if (quickReply.shortcut !== undefined) updateData.shortcut = quickReply.shortcut;
        if (quickReply.message !== undefined) updateData.message = quickReply.message;

        const { data, error } = await supabase
            .from('quick_replies')
            .update(updateData)
            .eq('id', id)
            .eq('organization_id', organizationId)
            .select()
            .single();

        if (error) {
            console.error('[QuickRepliesRepository] Error updating quick reply:', error);
            throw new Error(`Erro ao atualizar resposta rápida: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async delete(id: string, organizationId: string): Promise<void> {
        const { error } = await supabase
            .from('quick_replies')
            .delete()
            .eq('id', id)
            .eq('organization_id', organizationId);

        if (error) {
            console.error('[QuickRepliesRepository] Error deleting quick reply:', error);
            throw new Error(`Erro ao deletar resposta rápida: ${error.message}`);
        }
    }

    private mapToEntity(data: any): QuickReply {
        return {
            id: data.id,
            shortcut: data.shortcut,
            message: data.message
        };
    }
}

export const quickRepliesRepository = new SupabaseQuickRepliesRepository();
